import { authSelectors } from 'ducks/auth'
import { orgSelectors } from 'ducks/orgs'
import { SellerIntegrationType } from 'opensolar-common'
import { REGISTER_LINK_MAPPING } from 'pages/ordering/detail/content/RegisterLink'
import { useGetSupplierConfig } from 'pages/ordering/hooks/useGetSupplierConfig'
import { DistributorConnectionStatusesType, HardwareSupplierFilterKeyType } from 'pages/ordering/type'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import useDistributorsAuthenticationStatus from 'services/useDistributorsAuthenticationStatus'
import { ExtendedHardwareSupplierConfig } from '../DistributorChip'

type GetDistributorConnectionStatusAndConfigResponse = {
  isChecking: boolean
  atLeastOneDistributorConnected: boolean
  enabledAndConnectedDistributors: HardwareSupplierFilterKeyType[] | undefined
  distributorConfigs: ExtendedHardwareSupplierConfig[]
}

// TODO we probably need to rewrite this. Most of the information is already available in the enabledHardwareDistributors selector.
// This still depends endpoints that are quite out of date.
const useGetDistributorConnectionStatusAndConfig = (
  selectedSupplier?: HardwareSupplierFilterKeyType
): GetDistributorConnectionStatusAndConfigResponse => {
  const orgId = useSelector(authSelectors.getOrgId) || 0
  const isAuthenticatedPro = useSelector(authSelectors.getIsAuthenticatedPro)
  const sellerIntegrations = useSelector(orgSelectors.getOrgSellerIntegrations)
  const outletEnabled = window.reduxStore?.getState()?.orgs?.org?.enable_outlet_integration
  const { getSupplierConfig } = useGetSupplierConfig()
  const enabledHardwareDistributors = useSelector(orgSelectors.getEnabledSellerIntegrationsConfig)
  const enabledHardwareDistributorFilterKeys = useSelector(orgSelectors.getEnabledHardwareDistributorFilterKeys)
  const distributorConnectionStatus: DistributorConnectionStatusesType = useDistributorsAuthenticationStatus({
    orgId,
    distributors: enabledHardwareDistributorFilterKeys,
  })

  const atLeastOneDistributorConnected = useMemo(() => {
    return outletEnabled || enabledHardwareDistributors.some((distributor) => distributor.isConnected)
  }, [enabledHardwareDistributors, outletEnabled])

  // True while hardware distributor metadata is still loading (InitSellerIntegrations fetch).
  const isChecking = useMemo(() => {
    if (!orgId || !isAuthenticatedPro) {
      return false
    }
    return sellerIntegrations === undefined
  }, [orgId, isAuthenticatedPro, sellerIntegrations])

  const enabledAndConnectedDistributors = useMemo(() => {
    const distributors: HardwareSupplierFilterKeyType[] = []

    for (const [key, { isAuthenticated }] of Object.entries(distributorConnectionStatus)) {
      if (isAuthenticated) {
        distributors.push(key)
      }
    }

    if (distributors.length > 0) {
      return distributors
    }

    return undefined
  }, [distributorConnectionStatus])

  const registrationUrl = (filterKey) => {
    switch (filterKey) {
      case 'city_plumbing':
        return 'https://share.hsforms.com/1ArJCBRFsSReMQzJtbzqw7gcne2p'
      case 'outlet':
        return REGISTER_LINK_MAPPING['outlet']
      default:
        return '#'
    }
  }

  const distributorConfigs = useMemo(() => {
    return enabledHardwareDistributors.map((item: SellerIntegrationType) => {
      const filterKey = item.filterKey
      const config = getSupplierConfig(filterKey)

      return {
        ...config,
        name: item.name,
        ...(item.logoUrl && { logoUrl: item.logoUrl }),
        isEnabled: true,
        isConnected: item.isConnected,
        isDefault: item.isDefault,
        isSelected: selectedSupplier === filterKey || selectedSupplier === undefined,
        isVisible: true,
        registrationUrl: registrationUrl(filterKey),
      }
    })
  }, [enabledHardwareDistributors, getSupplierConfig, selectedSupplier])

  return {
    isChecking,
    atLeastOneDistributorConnected,
    enabledAndConnectedDistributors: enabledAndConnectedDistributors,
    distributorConfigs: distributorConfigs as ExtendedHardwareSupplierConfig[],
  }
}

export default useGetDistributorConnectionStatusAndConfig
